import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Link as MuiLink,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";


import axiosInstance from "../../config/axiosConfig";

const initialFormState = {
  shortName: "",
  longName: "",
  link: "",
  idUser: "",
};

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(initialFormState);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/Application");
      setApplications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar aplicaciones:", error);
      showSnackbar("Error al obtener el listado de aplicaciones", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchResponsibles = async () => {
    try {
      const response = await axiosInstance.get("/Application/responsibles");
      setResponsibles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar responsables:", error);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchResponsibles();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setSelectedId(null);
    setFormData(initialFormState);
    setOpenModal(true);
  };

  const handleOpenEdit = (row) => {
    setIsEditing(true);
    setSelectedId(row.id);
    setFormData({
      shortName: row.shortName || "",
      longName: row.longName || "",
      link: row.link || "",
      idUser: row.idUser || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData(initialFormState);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.shortName.trim()) {
      showSnackbar("Las siglas de la aplicación son obligatorias", "warning");
      return;
    }

    if (!formData.idUser) {
      showSnackbar("Debe seleccionar un responsable obligatorio", "warning");
      return;
    }

    const payload = {
      shortName: formData.shortName.trim(),
      longName: formData.longName.trim(),
      link: formData.link.trim(),
      idUser: Number(formData.idUser), // Obligatorio
    };

    try {
      if (isEditing) {
        await axiosInstance.put(`/Application/${selectedId}`, payload);
        showSnackbar("Aplicación modificada exitosamente.");
      } else {
        await axiosInstance.post("/Application", payload);
        showSnackbar("Aplicación agregada exitosamente.");
      }
      handleCloseModal();
      fetchApplications();
    } catch (error) {
      console.error("Error al guardar aplicación:", error);
      const errMsg =
        error.response?.data?.message || "Ocurrió un error al procesar la solicitud.";
      showSnackbar(errMsg, "error");
    }
  };

  const columns = [
    {
      field: "shortName",
      headerName: "SIGLAS",
      width: 180,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "longName",
      headerName: "Descripción",
      minWidth: 260,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "link",
      headerName: "Enlace",
      minWidth: 200,
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params.value) return <span style={{ color: "#999" }}>—</span>;
        const href = params.value.startsWith("http")
          ? params.value
          : `https://${params.value}`;
        return (
          <MuiLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {params.value}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </MuiLink>
        );
      },
    },
    {
      field: "responsibleName",
      headerName: "Responsable",
      minWidth: 240,
      disableColumnMenu: true,
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Modificar Aplicación">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const filteredApplications = applications.filter((app) => {
    if (!searchText.trim()) return true;
    const term = searchText.toLowerCase();
    return (
      (app.shortName && app.shortName.toLowerCase().includes(term)) ||
      (app.longName && app.longName.toLowerCase().includes(term)) ||
      (app.responsibleName && app.responsibleName.toLowerCase().includes(term)) ||
      (app.link && app.link.toLowerCase().includes(term))
    );
  });

  return (
    <Box sx={{ width: "100%", p: 3, boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
          Aplicaciones
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Buscar aplicación..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
            }}
            sx={{ minWidth: 250 }}
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchApplications}
            disabled={loading}
          >
            Actualizar
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
          >
            Nueva Aplicación
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 600, width: "100%", bgcolor: "background.paper" }}>
        <DataGrid
          rows={filteredApplications}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Modal Agregar / Modificar */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <form onSubmit={handleSave}>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {isEditing ? "Modificar Aplicación" : "Agregar Nueva Aplicación"}
          </DialogTitle>

          <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
            <TextField
              name="shortName"
              label="SIGLAS"
              placeholder="Ej: SIMSAN, SIGESAN, etc."
              value={formData.shortName}
              onChange={handleInputChange}
              required
              fullWidth
              autoFocus
            />

            <TextField
              name="longName"
              label="Descripción"
              placeholder="Descripción de la aplicación"
              value={formData.longName}
              onChange={handleInputChange}
              multiline
              rows={2}
              fullWidth
            />

            <TextField
              name="link"
              label="Enlace (URL)"
              placeholder="https://..."
              value={formData.link}
              onChange={handleInputChange}
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel id="responsible-select-label">Responsable Asignado</InputLabel>
              <Select
                labelId="responsible-select-label"
                name="idUser"
                value={formData.idUser}
                label="Responsable asignado *"
                onChange={handleInputChange}
              >
                <MenuItem value="" disabled>
                  <em>-- Seleccione un responsable (obligatorio) --</em>
                </MenuItem>
                {responsibles.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.fullName} 
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseModal} color="inherit">
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              {isEditing ? "Guardar Cambios" : "Agregar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}